// Contracts — work.weern.com GENERATOR module (2026-06-23). Ported from the
// pothos generator endpoints onto the work backend. Generators upload screenshots
// (membership / gift_card / event_ticket / coupon), choosing locale + country +
// merchant + category. Shapes snapped from the ported uploadAsset / generator.ts.
import { z } from 'zod';

export const WorkGeneratorCategorySchema = z.enum(['membership', 'event_ticket', 'gift_card', 'coupon']);
export type WorkGeneratorCategory = z.infer<typeof WorkGeneratorCategorySchema>;

// POST /api/work/generator/upload — one generator screenshot.
export const WorkGeneratorUploadReqSchema = z.object({
  merchant: z.string().trim().min(1).max(200),
  locale: z.string().trim().min(1).max(16),            // legacy 2-letter OR xx_XX
  merchant_region: z.string().trim().min(1).max(8),    // ISO-2 country
  category: WorkGeneratorCategorySchema,
  fileName: z.string().trim().max(200).default('screenshot.png'),
  mimeType: z.string().trim().min(1).max(100),
  fileBase64: z.string().trim().min(1).max(9_000_000),
});
export type WorkGeneratorUploadReq = z.infer<typeof WorkGeneratorUploadReqSchema>;

// Success → { ok:true, expected_payout_usd, registration }. Synchronous reject
// (decoder found no code) → { ok:false, error, reason }. (Domain pre-checks like
// merchant_taken / subcat_full return a separate {error_key,...} 400, not this.)
export const WorkGeneratorUploadResSchema = z.object({
  ok: z.boolean(),
  error: z.string().optional(),
  reason: z.string().optional(),
  expected_payout_usd: z.number().optional(),
  registration: z
    .object({
      id: z.string(),
      registered_at: z.string(),
      locale: z.string(),
      merchant: z.string(),
      merchant_region: z.string(),
      category: WorkGeneratorCategorySchema,
      modality: z.string(),
      asset_capacity: z.boolean(),
      evaluation_status: z.string(),
      r2_key: z.string(),
      file_name: z.string(),
      pack_slots: z.number().optional(),          // 20−used for a seed; 1 for coupon/ticket
      merchant_used_after: z.number().optional(), // merchant slots filled after this upload
    })
    .optional(),
});
export type WorkGeneratorUploadRes = z.infer<typeof WorkGeneratorUploadResSchema>;

// GET /api/work/generator/locales — pickable locales (non-saturated), with
// per-locale remaining capacity + country region CSV.
export const WorkGeneratorLocaleSchema = z.object({
  code: z.string(),
  name: z.string(),
  region: z.string(),               // CSV of ISO-2 countries
  priority: z.string(),
  remaining: z.number().int().nonnegative(),
  merchants_left: z.number().int().nonnegative(),
  status: z.enum(['yellow', 'green']),
});
export type WorkGeneratorLocale = z.infer<typeof WorkGeneratorLocaleSchema>;

export const WorkGeneratorLocalesResSchema = z.object({
  locales: z.array(WorkGeneratorLocaleSchema),
});
export type WorkGeneratorLocalesRes = z.infer<typeof WorkGeneratorLocalesResSchema>;

// GET /api/work/generator/quota?locale=<> — membership-screenshot quota for one locale.
export const WorkGeneratorQuotaResSchema = z.object({
  locale: z.string(),
  used: z.number().int().nonnegative(),
  remaining: z.number().int().nonnegative(),
  merchants_left: z.number().int().nonnegative(),
  status: z.enum(['green', 'yellow', 'red']),
});
export type WorkGeneratorQuotaRes = z.infer<typeof WorkGeneratorQuotaResSchema>;

// GET /api/work/generator/me — slim earnings summary for the generator home card.
export const WorkGeneratorMeResSchema = z.object({
  earned_total: z.number(),           // lifetime credited (incl. bonuses)
  cards_total: z.number().int().nonnegative(),
  cards_today: z.number().int().nonnegative().default(0), // today's upload count (for the count-only block)
  per_card: z.number(),               // cfg base credit per card
  today_earned: z.number(),
  show_bonus_amounts: z.boolean(),    // false (Sultan) → hide $ in UI
});
export type WorkGeneratorMeRes = z.infer<typeof WorkGeneratorMeResSchema>;
